import { Table } from '@tanstack/react-table';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import ChevronLeft from '@/assets/icons/chevron-sm-left.svg';
import ChevronRight from '@/assets/icons/chevron-sm-right.svg';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Typography } from '@/components/ui/typography';
import {
  ROWS_PER_PAGE_OPTIONS,
  useTableContext,
} from '@/contexts/table-context';
import { usePagination } from '@/hooks/use-pagination';

interface PaginationProps<TData> {
  table: Table<TData>;
  // Lets a table narrow the selectable page sizes (e.g. when its API caps
  // per_page below the largest default option). Defaults to the full set.
  rowsPerPageOptions?: number[];
}

export function Pagination<TData>({
  table,
  rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS,
}: PaginationProps<TData>) {
  const { generatedPageNumbers } = usePagination(table);
  const { setPagination } = useTableContext();

  const { pageIndex, pageSize } = table.getState().pagination;
  const rowCount = table.getRowCount();
  const from = pageIndex * pageSize + 1;
  const to = Math.min((pageIndex + 1) * pageSize, rowCount);

  const setPage = (pageNumber: number | '...') => {
    if (pageNumber !== '...') table.setPageIndex(pageNumber - 1);
  };

  const handleRowsPerPageChange = (value: string) => {
    // Reset to the first page so the new page size can't land the user
    // out of range of the (now smaller) total page count.
    setPagination({ pageIndex: 0, pageSize: Number(value) });
  };

  const handlePageInputChange = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== 'Enter') return;

    const value = parseInt(event.currentTarget.value, 10);

    if (value < 1 || value > table.getPageCount()) {
      // TODO: Show toaster to enter a valid page number
      return;
    }

    setPage(value);
  };

  return (
    <div className="flex justify-between">
      <div className="flex items-center gap-6">
        <Typography variant="subtitle1">{`${from} - ${to} of ${rowCount}`}</Typography>
        <div className="flex items-center gap-2">
          <Typography variant="subtitle1">
            <FormattedMessage
              defaultMessage="Rows per page"
              description="Message shown next to the rows-per-page dropdown"
            />
          </Typography>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="secondary"
                size="sm"
                className="gap-1.5 rounded-sm"
              >
                <Typography variant="subtitle1">{pageSize}</Typography>
                <ChevronDown className="text-fx-neutral-400 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuRadioGroup
                value={String(pageSize)}
                onValueChange={handleRowsPerPageChange}
              >
                {rowsPerPageOptions.map(option => (
                  <DropdownMenuRadioItem key={option} value={String(option)}>
                    {option}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Typography variant="subtitle1">
            <FormattedMessage
              defaultMessage="Go to page"
              description="Message shown for the go to page input"
            />
          </Typography>
          <Input
            type="text"
            className="!w-12"
            onKeyDown={handlePageInputChange}
          />
        </div>
        <div className="flex gap-3">
          <Button
            variant="secondary"
            size="icon"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <img src={ChevronLeft} alt="Chevron left" />
          </Button>
          <div className="flex">
            {generatedPageNumbers.map((pageNumber, index) => {
              const isSelected =
                pageNumber === table.getState().pagination.pageIndex + 1;

              return (
                <Button
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${pageNumber}-${index}`}
                  variant="secondary"
                  size="icon"
                  onClick={() => setPage(pageNumber)}
                  className={clsx(
                    'rounded-none first:ml-0 hover:relative hover:z-10',
                    index === 0 && 'rounded-l-lg',
                    index === generatedPageNumbers.length - 1 && 'rounded-r-lg',
                    !isSelected && '-ml-px',
                    isSelected &&
                      'bg-fx-brand-100 border-fx-primary relative z-10',
                    pageNumber === '...' && 'pointer-events-none cursor-auto',
                  )}
                >
                  <Typography
                    variant="subtitle1"
                    className={clsx(isSelected ? '!text-fx-brand-600' : '')}
                  >
                    {pageNumber}
                  </Typography>
                </Button>
              );
            })}
          </div>
          <Button
            variant="secondary"
            size="icon"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <img src={ChevronRight} alt="Chevron left" />
          </Button>
        </div>
      </div>
    </div>
  );
}
